import { AssignmentLate, CheckCircle, Delete, MoreVert, Report, Visibility } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { MyModal } from "components";
import { ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { ComponentType, MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { IncidentAlertLink } from "pages/incidents/alertLink";
import { Link } from "react-router-dom";
import { ThreatModel, ThreatStatusTypes } from "types";
import { canAccessSite, canManageSite } from "utils";
import ThreatDetails from "./details";

interface IThreatActions {
  threat: ThreatModel;
  onSuccess?: () => void;
}

const STATUS_ACTIONS: {
  status: ThreatStatusTypes;
  icon: ComponentType<{ sx?: object; fontSize?: "small" | "inherit" | "medium" | "large" }>;
  iconBg: string;
  iconColor: string;
  labelKey: string;
}[] = [
  {
    status: "Action Required",
    icon: AssignmentLate,
    iconBg: "rgba(255, 77, 94, 0.14)",
    iconColor: COLORS.error,
    labelKey: "Action Required"
  },
  {
    status: "Resolved",
    icon: CheckCircle,
    iconBg: "rgba(9, 185, 133, 0.14)",
    iconColor: COLORS.success,
    labelKey: "Resolved"
  },
  {
    status: "Reported",
    icon: Report,
    iconBg: "#F5F3FF",
    iconColor: "#8B5CF6",
    labelKey: "Reported"
  }
];

const menuPaperSx = {
  minWidth: 220,
  py: 0.5,
  bgcolor: COLORS.surface.card,
  borderRadius: `${COLORS.radius.md}px`,
  border: `1px solid ${COLORS.surface.border}`,
  boxShadow: COLORS.shadow.card
};

const menuSectionHeaderSx = {
  px: 1.5,
  py: 0.75,
  lineHeight: 1.4,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: COLORS.surface.mutedText,
  bgcolor: "transparent"
};

const menuItemSx = {
  px: 1.5,
  py: 1,
  minHeight: 40,
  borderRadius: `${COLORS.radius.sm}px`,
  mx: 0.5,
  color: COLORS.surface.primaryText,
  "&:hover": { bgcolor: COLORS.surface.tableHeader }
};

function getThreatSiteId(threat: ThreatModel): string | undefined {
  return threat.box?.siteId?.toString() || threat.box?.site?._id?.toString();
}

export default function ThreatActions({ threat, onSuccess }: IThreatActions) {
  const [{ userData }] = useContextController();
  const siteId = getThreatSiteId(threat);
  const canAccess = canAccessSite(userData, siteId);
  const canDelete = canManageSite(userData, siteId);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const [statusOpen, setStatusOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState<ThreatStatusTypes | null>(null);
  const [note, setNote] = useState("");
  const {
    isLoading: isStatusLoading,
    response: statusResponse,
    error: statusError,
    callFetch: callFetchPut
  } = useFetch("PUT");

  const [removeOpen, setRemoveOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const {
    isLoading: isDeleteLoading,
    response: deleteResponse,
    error: deleteError,
    callFetch: callFetchDelete
  } = useFetch("DELETE");

  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const availableStatusActions = useMemo(
    () => STATUS_ACTIONS.filter(({ status }) => status !== threat.status),
    [threat.status]
  );

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const openStatusNote = useCallback(
    (status: ThreatStatusTypes) => {
      handleMenuClose();
      setTargetStatus(status);
      setNote("");
      setStatusOpen(true);
    },
    [handleMenuClose]
  );

  const openRemove = useCallback(() => {
    handleMenuClose();
    setChecked(false);
    setRemoveOpen(true);
  }, [handleMenuClose]);

  const onStatusSubmit = useCallback(() => {
    if (!threat || !targetStatus || !note.trim()) return;
    callFetchPut(ENDPOINTS.threats + `/${threat._id}`, {
      body: JSON.stringify({ status: targetStatus, note: note.trim() })
    }).catch(console.error);
  }, [threat, targetStatus, note, callFetchPut]);

  const onDeleteSubmit = useCallback(() => {
    callFetchDelete(ENDPOINTS.threats + `/${threat._id}`).catch(console.error);
  }, [threat, callFetchDelete]);

  useEffect(() => {
    if (statusResponse) {
      enqueueSnackbar(statusResponse.message || "", { variant: "success" });
      setStatusOpen(false);
      setTargetStatus(null);
      setNote("");
      onSuccess?.();
    }
  }, [statusResponse, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (statusError) {
      enqueueSnackbar(statusError.message || "", { variant: "error" });
    }
  }, [statusError, enqueueSnackbar]);

  useEffect(() => {
    if (deleteResponse) {
      enqueueSnackbar(deleteResponse.message || "", { variant: "success" });
      setRemoveOpen(false);
      onSuccess?.();
    }
  }, [deleteResponse, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (deleteError) {
      enqueueSnackbar(deleteError.message || "", { variant: "error" });
    }
  }, [deleteError, enqueueSnackbar]);

  if (!canAccess) {
    return null;
  }

  return (
    <Box>
      <Tooltip title={t["Actions"]}>
        <IconButton
          aria-label={t["Actions"]}
          aria-controls={menuOpen ? `threat-actions-${threat._id}` : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={handleMenuOpen}
          size="small"
          sx={{
            width: 36,
            height: 36,
            color: COLORS.surface.secondaryText,
            borderRadius: `${COLORS.radius.sm}px`,
            border: `1px solid ${COLORS.surface.border}`,
            bgcolor: COLORS.surface.card,
            "&:hover": { bgcolor: COLORS.surface.tableHeader }
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        id={`threat-actions-${threat._id}`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: menuPaperSx }}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
      >
        {availableStatusActions.length > 0 && (
          <>
            <ListSubheader disableSticky sx={menuSectionHeaderSx}>
              {t["Change Status"]}
            </ListSubheader>
            {availableStatusActions.map(({ status, icon: Icon, iconBg, iconColor, labelKey }) => (
              <MenuItem key={status} onClick={() => openStatusNote(status)} sx={menuItemSx}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: `${COLORS.radius.sm}px`,
                      bgcolor: iconBg,
                      color: iconColor
                    }}
                  >
                    <Icon sx={{ fontSize: 16 }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={t[labelKey]}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: COLORS.surface.primaryText }}
                />
              </MenuItem>
            ))}
            <Divider sx={{ my: 0.5, borderColor: COLORS.surface.border }} />
          </>
        )}
        <ListSubheader disableSticky sx={menuSectionHeaderSx}>
          {t["Actions"]}
        </ListSubheader>
        <IncidentAlertLink threat={threat} variant="menu" onDone={handleMenuClose} />
        <MenuItem component={Link} to={"/threats/" + threat._id} onClick={handleMenuClose} sx={menuItemSx}>
          <ListItemIcon sx={{ minWidth: 32, color: COLORS.surface.secondaryText }}>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t["View"]}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: COLORS.surface.primaryText }}
          />
        </MenuItem>
        {canDelete && (
          <MenuItem
            onClick={openRemove}
            sx={{
              ...menuItemSx,
              color: COLORS.error,
              "&:hover": { bgcolor: "rgba(255, 77, 94, 0.08)" }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: COLORS.error }}>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t["Delete"]} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
          </MenuItem>
        )}
      </Menu>

      <MyModal
        title={targetStatus ? `${t["Change Status"]}: ${t[targetStatus]}` : t["Change Status"]}
        subtitle={t["Add a note to document the reason for this status change."]}
        open={statusOpen}
        setOpen={setStatusOpen}
        variant="light"
      >
        <TextField
          label={t["Note"]}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          required
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: COLORS.surface.card,
              color: COLORS.surface.primaryText,
              borderRadius: `${COLORS.radius.md}px`,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.border },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.secondaryText },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.primary }
            },
            "& .MuiInputLabel-root": {
              color: COLORS.surface.secondaryText,
              fontWeight: 500,
              "&.Mui-focused": { color: COLORS.primary }
            }
          }}
        />
        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => setStatusOpen(false)}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!note.trim() || isStatusLoading}
            endIcon={isStatusLoading ? <CircularProgress size={18} /> : undefined}
            onClick={onStatusSubmit}
            sx={{
              minWidth: 140,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              ...primaryContainedButtonSx
            }}
          >
            {t["Submit"]}
          </Button>
        </Box>
      </MyModal>

      <MyModal
        title={t["Remove Threat"]}
        subtitle={t["Are You Sure You Want to Remove?"]}
        open={removeOpen}
        setOpen={setRemoveOpen}
        variant="light"
        width={680}
      >
        <ThreatDetails threat={threat} />
        <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(_, value) => setChecked(value)}
                sx={{
                  color: COLORS.surface.secondaryText,
                  "&.Mui-checked": { color: COLORS.error }
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 14, color: COLORS.surface.secondaryText }}>
                {t["I confirm that removing this is an irreversible action and i want to proceed."]}
              </Typography>
            }
          />
        </FormControl>
        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
          <Button
            type="button"
            variant="outlined"
            onClick={() => setRemoveOpen(false)}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            variant="contained"
            disabled={!checked || isDeleteLoading}
            endIcon={isDeleteLoading ? <CircularProgress size={18} /> : undefined}
            onClick={onDeleteSubmit}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              bgcolor: COLORS.error,
              boxShadow: "none",
              "&:hover": { bgcolor: COLORS.error, filter: "brightness(1.05)", boxShadow: "none" }
            }}
          >
            {t["Remove"]}
          </Button>
        </Box>
      </MyModal>
    </Box>
  );
}
